import styled from "styled-components";
import BackgroundCover from "@assets/images/background.svg";
import backgroundImage from "@assets/images/background-image.jpg";
import CheckCircle from "@assets/icons/check-circle.svg";
import Logo from "@assets/icons/health-nz-logo.svg";
import { Button } from "@te-whatu-ora/anatomic";
import { useNavigate } from "react-router-dom";
import { Analytics, HealthcareAnalytics, useScrollDepthTracking } from "@utils/analytics";
import mediaQuery from "@utils/mediaQuery";
import Disclaimer from "@components/Disclaimer";
import EndorsingOrganisations from "@components/EndorsingOrganisations";
import AppVersion from "@components/AppVersion";
import {
  STORAGE_KEYS,
  getLocalStorage,
  setLocalStorage,
} from "@utils/localStorage";
import { getDiffInDays } from "@utils/date";
import { useEffect, useMemo, useState } from "react";

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  margin: 0 auto;
  margin-bottom: 16px;
  margin-top: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
  width: 100%;

  ${mediaQuery("tablet")`
    padding: 77px 100px;
    max-width: 1320px;
    flex-direction: row;
    gap: 16px;
    align-items: flex-start;
  `}
`;

const Title = styled.h1`
  margin-bottom: 17px;
`;

const Subheading = styled.p`
  margin: 0;
  font-size: var(--font-size-3xl);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 23.4px;
  margin-bottom: 30px;

  ${mediaQuery("tablet")`
  font-size: var(--font-size-2xl);
  `}
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;

  ${mediaQuery("tablet")`
  flex-direction: column;
  `}
`;

const TextWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  margin-bottom: 30px;
  order: 1;
`;

const InfoText = styled.p`
  margin: 0;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: #505e79;
  font-size: var(--font-size-xl);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 24.8px;

  ${mediaQuery("tablet")`
  align-items: center;
  font-size: var(--font-size-xl);

  span {
    display: flex;
    align-items: center;
  }
  `}
`;

const BackgroundImageWrapper = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  margin-bottom: 20px;
  margin-top: 20px;
  z-index: 1;
  display: none;

  ${mediaQuery("desktop")`
  margin-top: 0;
  margin-bottom: 0;
  width: 100%;
  height: 100%;
  display: initial;
  `}
`;

const ButtonWrapper = styled.div`
  margin-bottom: 32px;
  order: 2;

  ${mediaQuery("tablet")`
  order: 1;
  `}
`;

const ApprovalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 333px;
  height: auto;
  font-size: var(--font-size-base);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-base);

  p {
    margin-top: 0;
    margin-bottom: 20px;
  }
`;

const BackgroundImageContainer = styled.div`
  display: none;

  ${mediaQuery("tablet")`
  display: block;
  svg {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    height: 200px;
  }
  `}
`;

const LeftContent = styled.div`
  flex: 1;
  z-index: 1;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Home = () => {
  const [showDiscaimer, setShowDiscaimer] = useState(false);
  const navigate = useNavigate();

  const currentDate = useMemo(() => new Date(), []);

  useEffect(() => {
    const cleanup = useScrollDepthTracking('home');
    return cleanup;
  }, []);

  useEffect(() => {
    const discaimerTimeStamp = new Date(
      getLocalStorage(STORAGE_KEYS.DISCAIMER_TIME_STAMP) as string
    );

    if (discaimerTimeStamp) {
      const diffInDays = getDiffInDays(currentDate, discaimerTimeStamp);
      setShowDiscaimer(diffInDays > 1);
    } else {
      setShowDiscaimer(true);
    }
  }, [currentDate]);

  return (
    <>
      <BackgroundImageContainer>
        <BackgroundCover />
      </BackgroundImageContainer>
      <Container>
        <LeftContent>
          <Title>Aotearoa antibiotic prescribing guidelines</Title>
          <Subheading>
            <p>
              Supporting with education regarding antibiotic prescribing best
              practices, common use cases, and complex dosing.
            </p>
            <p>
              <i>
                <b>Disclaimer:</b> These are educational
                guidelines and do not supplant clinical judgement or
                Infectious Diseases/Clinical Microbiology
                consultation.
              </i>
            </p>
          </Subheading>
          <InfoContainer>
            <TextWrapper>
              <InfoText>
                <span>
                  <CheckCircle />
                </span>
                {/* */}
                Educational guidelines for common conditions
              </InfoText>
              <InfoText>
                <span>
                  <CheckCircle />
                </span>
                {/* */}
                Information on complex dosing
              </InfoText>
              <InfoText>
                <span>
                  <CheckCircle />
                </span>
                {/* */}
                Contact your local ID/Micro expert for advice
              </InfoText>
            </TextWrapper>
            <ButtonWrapper>
              <Button onClick={() => {
                Analytics.trackButtonClick('go_to_guidelines', 'home_page', {
                  destination: '/guidelines',
                  button_text: 'Go to guidelines'
                });
                navigate("/guidelines");
              }}>
                Go to guidelines
              </Button>
            </ButtonWrapper>
          </InfoContainer>
        </LeftContent>

        <BackgroundImageWrapper>
          <img src={backgroundImage} alt="" width={"100%"} height={"100%"} />
        </BackgroundImageWrapper>

        {showDiscaimer && (
          <Disclaimer
            title="Disclaimer"
            content={`These guidelines are intended to support healthcare professionals in Aotearoa New Zealand with clinical decision-making. They offer guidance on the optimal selection of antimicrobial regimens, including situations where antimicrobials are not indicated, for common conditions in typical patients.  
The guidelines do not replace clinical judgment or specialist advice.  Responsibility for using, interpreting and applying the guidelines lies with the user.  
The guidelines are based on available evidence and expert opinion.  While efforts are made to ensure currency, content may not always reflect evolving evidence.  Feedback on new information and ideas for improvement is welcome.
External links to third-party sites are provided for convenience but responsibility for this content rests with the relevant third party.
Analytics will be used responsibly for quality improvement, education and research.`}
            buttonText="Accept"
            buttonOnClick={() => {
              HealthcareAnalytics.trackDisclaimerAcceptance();
              setLocalStorage(
                STORAGE_KEYS.DISCAIMER_TIME_STAMP,
                currentDate.toString()
              );
              setShowDiscaimer(false);
            }}
          />
        )}
      </Container>
      <EndorsingOrganisations />
      <AppVersion />
    </>
  );
};

export default Home;